<template>
    <v-combobox
        :model-value="modelValue"
        v-model:search="search"
        :items="pageItems"
        item-title="title"
        item-value="value"
        :label="label"
        :variant="variant"
        density="compact"
        hide-details="auto"
        clearable
        hide-no-data
        no-filter
        autocomplete="off"
        prepend-inner-icon="mdi-auto-fix"
        :menu-props="menuProps"
        @update:model-value="onModelChange"
        @update:search="onSearch"
        @update:menu="onMenuToggle"
        @blur="$emit('blur')"
    >
        <template #item="{ props: itemProps }">
            <v-list-item
                v-bind="itemProps"
                class="product-code-suggest-item"
            />
        </template>
        <template
            v-if="hasMore"
            #append-item
        >
            <div
                ref="loadMoreSentinel"
                class="product-code-suggest-sentinel"
                aria-hidden="true"
            />
        </template>
    </v-combobox>
</template>

<script>
import { debounce } from 'lodash';
import {
    PRODUCT_CODE_SUGGEST_DEBOUNCE_MS,
    PRODUCT_CODE_SUGGEST_PAGE_SIZE,
    PRODUCT_CODE_SUGGEST_SCROLL_THRESHOLD_PX,
    buildProductCodeSuggestOptions,
    collectProductCodeSuggestMatches,
    isProductCodeSuggestSearchEcho,
    resolveProductCodeSuggestValue,
} from '@/utils/productCodeSuggest';

const MENU_CONTENT_CLASS = 'product-code-suggest-menu';

export default {
    name: 'ProductCodeSuggestCombobox',
    props: {
        modelValue: {
            type: [String, Object, null],
            default: '',
        },
        products: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: 'Cod produs (autocompletare)',
        },
        variant: {
            type: String,
            default: 'outlined',
        },
    },
    emits: ['update:modelValue', 'select-existing', 'blur'],
    data() {
        return {
            search: '',
            pageItems: [],
            activeQuery: '',
            offset: 0,
            hasMore: false,
            loadingMore: false,
            ignoreSearchUntil: 0,
            committedCode: '',
            slimOptions: [],
            menuOpen: false,
            menuScrollEl: null,
            menuScrollHandler: null,
            menuWheelHandler: null,
            loadMoreObserver: null,
            attachRetryId: null,
        };
    },
    computed: {
        menuProps() {
            return {
                // Keep Vuetify's combobox content class or overflow/flex scrolling breaks.
                contentClass: ['v-combobox__content', MENU_CONTENT_CLASS],
                maxHeight: 310,
                maxWidth: 0,
            };
        },
    },
    watch: {
        products: {
            immediate: true,
            handler(products) {
                this.slimOptions = buildProductCodeSuggestOptions(products);
                if (this.activeQuery) {
                    this.resetPage(this.activeQuery);
                }
            },
        },
    },
    created() {
        this.debouncedResetPage = debounce((query) => {
            this.resetPage(query);
        }, PRODUCT_CODE_SUGGEST_DEBOUNCE_MS);
    },
    beforeUnmount() {
        this.debouncedResetPage?.cancel();
        this.teardownInfiniteScroll();
    },
    methods: {
        clearPage() {
            this.teardownInfiniteScroll();
            this.pageItems = [];
            this.activeQuery = '';
            this.offset = 0;
            this.hasMore = false;
            this.loadingMore = false;
        },
        resetPage(search) {
            const query = String(search || '').trim().toLowerCase();
            if (!query) {
                this.clearPage();
                return;
            }

            const { matches, hasMore } = collectProductCodeSuggestMatches(
                this.slimOptions,
                query,
                { offset: 0, limit: PRODUCT_CODE_SUGGEST_PAGE_SIZE },
            );
            this.activeQuery = query;
            this.pageItems = matches;
            this.offset = matches.length;
            this.hasMore = hasMore;
            this.loadingMore = false;
            this.scheduleAttachInfiniteScroll();
        },
        loadMore() {
            if (!this.hasMore || this.loadingMore || !this.activeQuery) {
                return;
            }

            this.loadingMore = true;
            const previousScrollEl = this.menuScrollEl;
            const previousScrollTop = previousScrollEl?.scrollTop ?? 0;

            const { matches, hasMore } = collectProductCodeSuggestMatches(
                this.slimOptions,
                this.activeQuery,
                { offset: this.offset, limit: PRODUCT_CODE_SUGGEST_PAGE_SIZE },
            );
            if (matches.length) {
                this.pageItems = [...this.pageItems, ...matches];
                this.offset += matches.length;
            }
            this.hasMore = hasMore;
            this.$nextTick(() => {
                this.loadingMore = false;
                this.scheduleAttachInfiniteScroll();
                // Keep the user's place after items are appended.
                this.$nextTick(() => {
                    const scrollEl = this.menuScrollEl || previousScrollEl;
                    if (scrollEl && previousScrollTop > 0) {
                        scrollEl.scrollTop = previousScrollTop;
                    }
                });
            });
        },
        getMenuRootEl() {
            return document.querySelector(`.${MENU_CONTENT_CLASS}`);
        },
        getMenuScrollEl() {
            const menu = this.getMenuRootEl();
            if (!menu) {
                return null;
            }

            const candidates = [
                menu.querySelector('.v-list'),
                menu.querySelector('.v-virtual-scroll'),
                menu,
            ].filter(Boolean);

            let best = null;
            let bestOverflow = -1;
            for (const element of candidates) {
                const style = window.getComputedStyle(element);
                const overflowY = style.overflowY;
                const canScrollY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
                if (!canScrollY) {
                    continue;
                }
                const overflowAmount = element.scrollHeight - element.clientHeight;
                if (overflowAmount > bestOverflow) {
                    best = element;
                    bestOverflow = overflowAmount;
                }
            }

            return best || menu.querySelector('.v-list') || menu;
        },
        isNearScrollBottom(scrollEl) {
            if (!scrollEl) {
                return false;
            }
            // No overflow yet → not near bottom from user scrolling.
            if (scrollEl.scrollHeight <= scrollEl.clientHeight + 1) {
                return false;
            }
            const remaining = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
            return remaining <= PRODUCT_CODE_SUGGEST_SCROLL_THRESHOLD_PX;
        },
        maybeLoadMoreFromScroll() {
            if (!this.hasMore || this.loadingMore) {
                return;
            }
            const scrollEl = this.menuScrollEl || this.getMenuScrollEl();
            if (this.isNearScrollBottom(scrollEl)) {
                this.loadMore();
            }
        },
        cancelAttachRetry() {
            if (this.attachRetryId != null) {
                cancelAnimationFrame(this.attachRetryId);
                this.attachRetryId = null;
            }
        },
        scheduleAttachInfiniteScroll(retries = 40) {
            this.cancelAttachRetry();
            this.attachRetryId = requestAnimationFrame(() => {
                this.attachRetryId = null;
                this.attachInfiniteScroll(retries);
            });
        },
        attachInfiniteScroll(retries = 0) {
            this.detachInfiniteScroll();
            if (!this.hasMore) {
                return;
            }
            if (!this.menuOpen) {
                if (retries > 0) {
                    this.scheduleAttachInfiniteScroll(retries - 1);
                }
                return;
            }

            const menu = this.getMenuRootEl();
            const scrollEl = this.getMenuScrollEl();
            const sentinel = this.$refs.loadMoreSentinel;
            if (!menu || !scrollEl || !(sentinel instanceof Element)) {
                if (retries > 0) {
                    this.scheduleAttachInfiniteScroll(retries - 1);
                }
                return;
            }

            this.menuScrollEl = scrollEl;
            this.menuScrollHandler = () => this.maybeLoadMoreFromScroll();
            this.menuWheelHandler = () => {
                requestAnimationFrame(() => this.maybeLoadMoreFromScroll());
            };

            scrollEl.addEventListener('scroll', this.menuScrollHandler, { passive: true });
            // Wheel bubbles; helps when the scroll container is nested oddly.
            menu.addEventListener('wheel', this.menuWheelHandler, { passive: true });

            this.loadMoreObserver = new IntersectionObserver(
                (entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) {
                        return;
                    }
                    this.maybeLoadMoreFromScroll();
                },
                {
                    root: scrollEl,
                    rootMargin: '0px',
                    threshold: 0,
                },
            );
            this.loadMoreObserver.observe(sentinel);
        },
        detachInfiniteScroll() {
            if (this.menuScrollEl && this.menuScrollHandler) {
                this.menuScrollEl.removeEventListener('scroll', this.menuScrollHandler);
            }
            const menu = this.getMenuRootEl();
            if (menu && this.menuWheelHandler) {
                menu.removeEventListener('wheel', this.menuWheelHandler);
            }
            this.menuScrollEl = null;
            this.menuScrollHandler = null;
            this.menuWheelHandler = null;

            if (this.loadMoreObserver) {
                this.loadMoreObserver.disconnect();
                this.loadMoreObserver = null;
            }
        },
        teardownInfiniteScroll() {
            this.cancelAttachRetry();
            this.detachInfiniteScroll();
        },
        onMenuToggle(open) {
            this.menuOpen = open;
            if (open) {
                this.scheduleAttachInfiniteScroll();
                return;
            }
            this.teardownInfiniteScroll();
        },
        onSearch(search) {
            const query = String(search || '').trim();
            const committed = this.committedCode || '';
            const isEcho = isProductCodeSuggestSearchEcho(search, committed || this.modelValue);

            if (Date.now() < this.ignoreSearchUntil && isEcho) {
                return;
            }

            // User edited the query away from the last selected code.
            if (committed && !isProductCodeSuggestSearchEcho(search, committed)) {
                this.committedCode = '';
                this.ignoreSearchUntil = 0;
            }

            this.search = search;

            if (!query) {
                this.debouncedResetPage.cancel();
                this.clearPage();
                return;
            }

            this.debouncedResetPage(search);
        },
        onModelChange(value) {
            const code = resolveProductCodeSuggestValue(value);
            this.$emit('update:modelValue', code);

            if (!code) {
                this.committedCode = '';
                this.ignoreSearchUntil = 0;
                this.clearPage();
                return;
            }

            const selectedExisting = this.slimOptions.some((option) => option.value === code);
            if (!selectedExisting) {
                return;
            }

            const isReselect = code === this.committedCode;
            this.committedCode = code;
            this.debouncedResetPage.cancel();
            // Always suppress post-select search/title echoes, including reselecting the same code.
            this.ignoreSearchUntil = Date.now() + 500;
            this.clearPage();
            this.search = code;

            if (!isReselect) {
                this.$emit('select-existing', code);
            }
        },
        reset() {
            this.debouncedResetPage.cancel();
            this.ignoreSearchUntil = 0;
            this.committedCode = '';
            this.search = '';
            this.clearPage();
        },
    },
};
</script>

<style>
.product-code-suggest-menu .v-list-item-title,
.product-code-suggest-item .v-list-item-title {
    white-space: normal;
    overflow-wrap: anywhere;
}

.product-code-suggest-sentinel {
    height: 1px;
    width: 100%;
    pointer-events: none;
}

/* Ensure the list is the scroll container inside the max-height menu. */
.product-code-suggest-menu.v-combobox__content > .v-sheet {
    display: flex;
    flex-direction: column;
    max-height: inherit;
}

.product-code-suggest-menu.v-combobox__content .v-list {
    flex: 1 1 auto;
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
}
</style>
